import { computed, Ref } from "vue";
import * as yup from "yup";
import { FormField } from "@/types/form";
import { CreatePostDisbursementChangeRequest , DisbursementVM, DisbursementStatusIds } from "@/types/disbursements";
import { CertificationVM } from "@/types/certifications";
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';

export interface PostDisbursementValidationData {
	disbursement: Ref<DisbursementVM>;
	certification: Ref<CertificationVM>;
	formData: Ref<Partial<CreatePostDisbursementChangeRequest>>;
	mode: Ref<"view" | "edit" | "add">;
	isEditMode: Ref<boolean>;
}

export function usePostDisbursementChangeForm(data: PostDisbursementValidationData) {
	const postDisbursementChangeReasons = computed(() => {
		return data.certification.value?.disbursementFormOptions?.postDisbursementChangeReasonTypes || [];
	});

	const disbursementMethods = computed(() => {
		return data.certification.value?.disbursementFormOptions?.disbursementMethodTypes || [];
	});

	const formFields = computed(() => {
		const fields: FormField[] = [
			{
				name: "disbursementAmount",
				label: "New Disb. Amount",
				type: "money",
				value: data.formData.value?.disbursementAmountDisplay,
				readonly: data.isEditMode.value,
				showOnAdd: true,
				showOnEdit: true,
				showOnView: false,
				disabledOnEdit: data.isEditMode.value,
				disabledOnAdd: false,
				hideLabel: false,
				width: "col-3",
				tooltip: true,
				tooltipText: data.isEditMode.value
					? 'Amount cannot be changed when editing'
					: 'Balance of disbursement amount post refund',
			},
			{
				name: "postDisbursementChangeReasonTypeId",
				label: "Post Disbursement Change Reason",
				type: "select",
				value: data.formData.value?.postDisbursementChangeReasonTypeId,
				options: postDisbursementChangeReasons.value,
				readonly: false,
				showOnAdd: true,
				showOnEdit: true,
				showOnView: false,
				disabledOnEdit: false,
				disabledOnAdd: data.disbursement.value.details.disbursementStatusTypeId === DisbursementStatusIds.POST_DISBURSEMENT_CANCELLATION && 
    				(data.formData.value?.disbursementAmount || 0) > 0,
				hideLabel: false,
				width: "col-9",
			},
			{
				name: "processingDate",
				label: "Processing Date",
				type: "date",
				value: data.formData.value?.processingDate,
				readonly: false,
				showOnAdd: true,
				showOnEdit: true,
				showOnView: false,
				disabledOnEdit: false,
				disabledOnAdd: false,
				hideLabel: false,
				width: "col-3",
				tooltip: true,
				tooltipText: "Date the refund is received by Student Choice.",
			},
			{
				name: "effectiveDate",
				label: "Effective Date",
				type: "date",
				value: data.formData.value?.effectiveDate,
				readonly: false,
				showOnAdd: true,
				showOnEdit: true,
				showOnView: false,
				disabledOnEdit: false,
				disabledOnAdd: false,
				hideLabel: false,
				width: "col-3",
				tooltip: true,
				tooltipText: "Date the refund should be effective at the servicer.",
			},
			{
				name: "settleDate",
				label: "Settle Date",
				type: "date",
				value: data.formData.value?.settleDate,
				readonly: false,
				showOnAdd: true,
				showOnEdit: true,
				showOnView: false,
				disabledOnEdit: false,
				disabledOnAdd: false,
				hideLabel: false,
				width: "col-3",
				tooltip: true,
				tooltipText: "Estimated date the funds should settle at the lender.",
			},
			{
				name: "disbursementMethodTypeId",
				label: "Method",
				type: "select",
				value: data.formData.value?.disbursementMethodTypeId,
				options: disbursementMethods.value,
				readonly: false,
				showOnAdd: true,
				showOnEdit: true,
				showOnView: false,
				disabledOnEdit: false,
				disabledOnAdd: false,
				hideLabel: false,
				width: "col-3",
				tooltip: true,
				tooltipText: "Method that Student Choice will receive the refund.",
			},
		];

		return fields.filter((field) => {
			if (data.mode.value === "edit" || data.mode.value === "add") {
				return field.showOnAdd !== false;
			}
			return field.showOnView === true;
		});
	});

	const validationSchema = computed(() => {
		const originalSettleDate = data.isEditMode.value 
		? DisbHelpers.formatDateYYYYMMDD(data.disbursement.value.details.latestPostDisbursementChange?.settleDate)
		: null;
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		if (data.isEditMode.value) {
			return yup.object().shape({			
			postDisbursementChangeReasonTypeId: yup
				.number()
				.required("Post Disbursement Change Reason is required"),
			processingDate: yup
				.string()
				.required("Processing Date is required"),
			effectiveDate: yup
				.string()
				.required("Effective Date is required"),
			settleDate: yup
				.string()
				.required("Settle Date is required")
				.test(
					'not-in-past',
					'Settle Date cannot be in the past',
					function(value) {						
						if (!value) return false;						
						if (originalSettleDate && originalSettleDate === value) {
							return true;
						}
						const valueDate = DisbHelpers.parseDateString(value);									
						return valueDate >= today;
					}
				),
			disbursementMethodTypeId: yup
				.number()
				.required("Method is required"),
			});
		}

		const existingDisbursementAmount = data.disbursement.value.details.disbursementAmount || 0;		
		const maxAmount = data.disbursement.value.details.maxAmountForPostDisbursementChange;
		return yup.object().shape({
			disbursementAmount: yup				
			.number()
			.required("New Disb. Amount is required")
			.min(0, "Amount cannot be negative")
			.max(maxAmount, `New Disbursement Amount cannot exceed ${DisbHelpers.formatCurrency(maxAmount)}`)			
			.test(
				'not-both-zero',
				'Disbursement Amount is already $0',
				function(value) {
					return !(existingDisbursementAmount === 0 && value === 0);
				}
			),
			postDisbursementChangeReasonTypeId: yup
				.number()
				.required("Post Disbursement Change Reason is required"),
			processingDate: yup
				.string()
				.required("Processing Date is required"),
			effectiveDate: yup
				.string()
				.required("Effective Date is required"),
			settleDate: yup
				.string()
				.required("Settle Date is required")
				.test(
					'not-in-past',
					'Settle Date cannot be in the past',
					function(value) {
						if (!value) return false;						
						const valueDate = DisbHelpers.parseDateString(value);						
						return valueDate >= today;
					}
				),
			disbursementMethodTypeId: yup
				.number()
				.required("Method is required"),
		});
	});

	return {
		formFields,
		validationSchema,
		postDisbursementChangeReasons,
		disbursementMethods,
	};
}